import { Injectable } from "@angular/core";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";

/** Simple AlertController & ToastController Implementation */
@Injectable({
  providedIn: "root",
})
export class TemplatePreviewService {
  readonly relativeFactor = 0.5;
  readonly fontSize = 12;
  readonly fontFamily = `'Helvetica Neue', Helvetica, Arial, sans-serif`;

  /** @ignore */
  constructor(private sanitizer: DomSanitizer) {}

  /**
   * Converts a tip tap `Document` into html and sanitizes it
   *
   * @param content tip tap `Document`
   * @param isDefaultTemplate selects to use the default JSON for previewing
   * @returns {SafeHtml}
   */
  getSafeHtml(
    content: any[],
    isDefaultTemplate: boolean,
    storageToken: string
  ): SafeHtml {
    let data = isDefaultTemplate ? defaultCoverPage : content;

    const rawHtml = this.buildHtml(data, storageToken);

    return this.sanitizer.bypassSecurityTrustHtml(
      `<div style="font-family: ${this.fontFamily};padding: 1.3rem; font-size: 0.5rem;">
${rawHtml}
</div>`
    );
  }

  private buildHtml(data: any[], storageToken: string): string {
    let html = "";

    try {
      // check if defaultCoverPage is array
      if (Array.isArray(data) && data.length > 0) {
        for (let i = 0; i < data.length; i++) {
          const el = data[i];

          if (el.type === "columns") {
            html += this.getColumn(el, storageToken);
          }

          if (el.type === "heading") {
            html += this.getHeading(el);
          }

          if (el.type === "paragraph") {
            html += this.getParagraph(el);
          }

          if (el.type === "horizontalLine") {
            html += this.getHorizontalLine(el);
          }

          if (el.type === "spacerBlock") {
            html += this.getSpacerBlock(el);
          }

          if (el.type === "imageBlock") {
            html += this.getImageBlock(el, storageToken);
          }

          if (el.type === "bulletList") {
            html += this.#getBulletList(el);
          }

          if (el.type === "orderedList") {
            html += this.#getOrderedList(el);
          }

          if (el.type === "signature") {
            html += this.#getSignatureBlock(el);
          }
        }
      }
    } catch (ex) {
      console.error(ex);
    }

    return html;
  }

  private getColumn(colData: TColumnsBlock, storageToken: string): string {
    let leftColumn = "";
    let rightColumn = "";
    let leftWidth = 50;
    let rightWidth = 50;

    try {
      if (colData.content.length > 0) {
        for (let i = 0; i < colData.content.length; i++) {
          let columnHtml = "";
          const el = colData.content[i];

          if (Array.isArray(el.content) && el.content.length > 0) {
            columnHtml = this.buildHtml(el.content, storageToken);
          }

          if (el.attrs.position === "left") {
            leftColumn += columnHtml;
          }
          if (el.attrs.position === "right") {
            rightColumn += columnHtml;
          }
        }
      }
    } catch (ex) {
      console.error(ex);
    }

    try {
      if (colData.attrs.layout === "sidebar-left") {
        leftWidth = 33;
        rightWidth = 67;
      }
      if (colData.attrs.layout === "sidebar-right") {
        leftWidth = 67;
        rightWidth = 33;
      }
    } catch (ex) {
      console.error(ex);
    }

    return `<div class="flex flex-row">
          <div style="width: ${leftWidth}%">${leftColumn}</div>
           <div style="width: ${rightWidth}%">${rightColumn}</div>
          </div>`;
  }

  private getHeading(data: THeading): string {
    try {
      const level = data?.attrs?.level ?? 1;
      const textAlign = data?.attrs?.textAlign ?? "left";
      const content = data?.content ?? [];

      let textSpans = "";

      if (Array.isArray(content) && content.length > 0) {
        textSpans += this.getTextSpans(content);
      }

      let headingSize = 0.5;

      switch (level) {
        case 1:
          headingSize = 1;
          break;
        case 2:
          headingSize = 0.8;
          break;
        case 3:
          headingSize = 0.6;
          break;
      }

      return `<h${level} style="font-weight: normal; font-family: ${this.fontFamily}; text-align: ${textAlign}; font-size: ${headingSize}rem; margin: 0.2rem 0px;">
${textSpans}
</h${level}>`;
    } catch (ex) {
      console.error(ex);
      return `<h2 style="font-size: ${1.1}rem"></h2>`;
    }
  }

  private getParagraph(data: TParagraph): string {
    try {
      const textAlign = data?.attrs?.textAlign ?? "left";
      const content = data?.content ?? [];

      let textSpans = "";

      if (Array.isArray(content) && content.length > 0) {
        textSpans += this.getTextSpans(content);
      } else {
        textSpans = "<span>&nbsp;</span>";
      }

      return `<p style="text-align: ${textAlign}; margin: 0px; color: black;">${textSpans}</p>`;
    } catch (ex) {
      console.error(ex);
      return `<p style="margin: 0px;"></p>`;
    }
  }

  private getHorizontalLine(data: THorizontalLine): string {
    try {
      const color = data?.attrs?.color ?? "black";
      const height = (data?.attrs?.height ?? 0.5) * this.relativeFactor;
      const width = data?.attrs?.width ?? 1;
      const alignment = data?.attrs?.alignment ?? "left";

      let flexAlign = "justify-content-start";
      if (alignment === "center") {
        flexAlign = "justify-center";
      } else if (alignment === "right") {
        flexAlign = "justify-content-end";
      }

      return `<div class="flex flex-row ${flexAlign}">
  <div style="height: ${height}px; width: ${width}%; background-color: ${color}; margin: 0.1rem 0px;"></div>
</div>`;
    } catch (ex) {
      console.error(ex);
      return `<div style="height: 3px; width: 100%; background-color: black; margin: 0.1rem 0px;"></div>`;
    }
  }

  #getSignatureBlock(data: TSignatureBlock): string {
    const content = data?.content ?? [];
    const textSpans = this.getTextSpans(content);
    return `<div class="w-full flex flex-row items-end h-[1rem]">
    <div>${textSpans}</div>
    <div class="flex-1 border-b-1 border-dotted border-neutral-300"></div>
    </div>`;
  }

  private getSpacerBlock(data: TSpacerBlock): string {
    try {
      const height = (data?.attrs?.height ?? 1) * this.relativeFactor;

      return `<div style="height: ${height}px; width: 100%;"></div>`;
    } catch (ex) {
      console.error(ex);
      return `<div style="height: 5px; width: 100%;"></div>`;
    }
  }

  private getImageBlock(data: TImageBlock, storageToken: string): string {
    try {
      const width = data?.attrs?.width ?? 1;
      const alignment = data?.attrs?.align ?? "left";
      const type = data?.attrs?.type ?? "";
      let imageSrc = data?.attrs?.src ?? "";

      if (imageSrc === "") {
        return `<div></div>`;
      }

      let flexAlign = "justify-content-start";
      if (alignment === "center") {
        flexAlign = "justify-center";
      } else if (alignment === "right") {
        flexAlign = "justify-content-end";
      }

      if (
        type !== ImageBlockTypes.PROJECT_IMAGE &&
        type !== ImageBlockTypes.LOGO
      ) {
        if (!imageSrc.includes("?")) {
          imageSrc += `?${storageToken}`;
        }
      }

      return `<div class="flex flex-row ${flexAlign}">
  <img style="width: ${width}; border-radius: 5px; background: var(--surface-200)" src="${imageSrc}" />
</div>`;
    } catch (ex) {
      console.error(ex);
      return `<div></div>`;
    }
  }

  private getTextSpans(content: (TTextSpan | TMentionSpan)[]): string {
    let spans = "";

    try {
      for (let i = 0; i < content.length; i++) {
        const item = content[i];
        const isBold =
          item.marks?.some((mark) => mark.type === "bold") ?? false;
        const isUnderline =
          item.marks?.some((mark) => mark.type === "underline") ?? false;
        const isItalic =
          item.marks?.some((mark) => mark.type === "italic") ?? false;
        const color =
          item.marks?.find((mark) => mark.type === "textStyle")?.attrs?.color ??
          "black";

        if (item.type === "text") {
          spans += `<span 
style="font-weight: ${isBold ? "bold" : "inherit"}; 
color: ${color}; 
font-style: ${isItalic ? "italic" : "normal"};
text-decoration: ${isUnderline ? "underline" : "none"}">${
            item.text ?? "&nbsp;"
          }</span>`;
        }

        if (item.type === "mention") {
          spans += `<span 
style="font-weight: ${isBold ? "bold" : "inherit"}; 
color: ${color}; 
font-style: ${isItalic ? "italic" : "normal"};
text-decoration: ${isUnderline ? "underline" : "none"}">${
            item.attrs.label
          }</span>`;
        }
      }
    } catch (ex) {
      console.error(ex);
    }

    return spans;
  }

  #getBulletList(content: TBulletList): string {
    const items = this.#getListItems(content.content);
    return `<ul class="list-disc list-outide ml-3">${items}</ul>`;
  }

  #getOrderedList(content: TOrderedList): string {
    const items = this.#getListItems(content.content);
    return `<ol class="list-decimal list-outide ml-2">${items}</ol>`;
  }

  /**
   * Retrieves a list of items from a TListItem array, with each item containing its corresponding
   * paragraph or heading content.
   * @param {TListItem[]} content - The array of list items to retrieve content from.
   * @returns {Content[]} - An array of content items.
   */
  #getListItems(content: TListItem[]): string {
    let items = "";

    for (let i = 0; i < content.length; i++) {
      const item = content[i];
      if (item.type === "listItem") {
        for (let j = 0; j < item.content.length; j++) {
          const listItem = item.content[j];
          if (listItem.type === "paragraph") {
            const para = this.getParagraph(listItem);
            items += `<li>${para}</li>`;
          }
          if (listItem.type === "heading") {
            // items.push(getHeading(listItem, false, false));
          }
          if (listItem.type === "bulletList") {
            // const innerList = this.#getBulletList(listItem);
            // items.push({
            //   ul: innerList,
            // });
          }
          if (listItem.type === "orderedList") {
            // const innerList = this.#getOrderedList(listItem);
            // items.push({
            //   ol: innerList,
            // });
          }
        }
      }
    }

    return items;
  }
}

type THeading = {
  type: "heading";
  attrs: {
    textAlign: "left" | "center" | "right";
    level: 1 | 2 | 3 | 4 | 5 | 6;
  };
  content: (TTextSpan | TMentionSpan)[];
};

type TParagraph = {
  type: "paragraph";
  attrs: {
    textAlign: "left" | "center" | "right";
  };
  content: (TTextSpan | TMentionSpan)[];
};

type THorizontalLine = {
  type: "horizontalLine";
  attrs: {
    color: string;
    height: number;
    width: number;
    alignment: "center" | "left" | "right";
  };
};

enum ImageBlockTypes {
  LOGO = "logo",
  PROJECT_IMAGE = "project-image",
  IMAGE = "image",
}

type TImageBlock = {
  type: "imageBlock";
  attrs: {
    src: string;
    /** as percentage */
    width: string;
    align: "center" | "left" | "right";
    type: ImageBlockTypes;
  };
};

type TTextSpan = {
  type: "text";
  marks: TTextMarks[];
  text: string;
};

type TMentionSpan = {
  type: "mention";
  marks: TTextMarks[];
  attrs: {
    label: string;
    id: string;
  };
};

type TTextMarks = {
  type: "bold" | "textStyle" | "italic" | "underline";
  attrs?: { color?: string };
};

type TSpacerBlock = {
  type: "spacerBlock";
  attrs: {
    height: number;
  };
};

type TColumnsBlock = {
  type: "columns";
  attrs: {
    layout: "sidebar-left" | "sidebar-right" | "two-column";
  };
  content: TColumnElement[];
};

type TColumnElement = {
  type: "column";
  attrs: {
    position: "left" | "right";
  };
  content: any[];
};

type TBulletList = {
  type: "bulletList";
  content: TListItem[];
};

type TOrderedList = {
  type: "orderedList";
  attrs: { start: number };
  content: TListItem[];
};

type TListItem = {
  type: "listItem";
  content: (THeading | TParagraph | TOrderedList | TBulletList)[];
};

type TSignatureBlock = {
  type: "signature";
  attrs: {};
  content: (TTextSpan | TMentionSpan)[];
};

const kImageLogoPlaceholder =
  "https://www.defectwise.com.au/wp-content/uploads/2024/11/transparent-logo-image-placeholder.png";

const kImageProjectPlaceholder =
  "https://www.defectwise.com.au/wp-content/uploads/2024/11/transparent-project-image-placeholder.png";

const defaultCoverPage: any[] = [
  {
    type: "columns",
    attrs: {
      layout: "sidebar-right",
    },
    content: [
      {
        type: "column",
        attrs: {
          position: "left",
        },
        content: [
          {
            type: "paragraph",
            attrs: {
              textAlign: "left",
            },
          },
        ],
      },
      {
        type: "column",
        attrs: {
          position: "right",
        },
        content: [
          {
            type: "imageBlock",
            attrs: {
              src: kImageLogoPlaceholder,
              width: "100%",
              align: "center",
              type: "logo",
            },
          },
          {
            type: "paragraph",
            attrs: {
              textAlign: "center",
            },
            content: [
              {
                type: "mention",
                attrs: {
                  id: "company",
                  label: "Company Name",
                },
                marks: [
                  {
                    type: "bold",
                  },
                ],
              },
              {
                type: "text",
                marks: [
                  {
                    type: "bold",
                  },
                ],
                text: " ",
              },
            ],
          },
        ],
      },
    ],
  },
  {
    type: "heading",
    attrs: {
      textAlign: "left",
      level: 1,
    },
    content: [
      {
        type: "text",
        text: "DEFECT REPORT",
      },
    ],
  },
  {
    type: "horizontalLine",
    attrs: {
      color: "#e0914c",
      height: 5,
      width: 100,
      alignment: "center",
    },
  },
  {
    type: "imageBlock",
    attrs: {
      src: kImageProjectPlaceholder,
      width: "100%",
      align: "center",
      type: "project-image",
    },
  },
  {
    type: "heading",
    attrs: {
      textAlign: "left",
      level: 3,
    },
    content: [
      {
        type: "mention",
        attrs: {
          id: "projectRef",
          label: "Project Ref",
        },
      },
      {
        type: "text",
        text: " - ",
      },
      {
        type: "mention",
        attrs: {
          id: "projectName",
          label: "Project Name",
        },
      },
      {
        type: "text",
        text: "  ",
      },
    ],
  },
  {
    type: "spacerBlock",
    attrs: {
      height: 10,
    },
  },
  {
    type: "paragraph",
    attrs: {
      textAlign: "left",
    },
    content: [
      {
        type: "text",
        marks: [
          {
            type: "bold",
          },
        ],
        text: "INSPECTION ADDRESS:",
      },
    ],
  },
  {
    type: "paragraph",
    attrs: {
      textAlign: "left",
    },
    content: [
      {
        type: "mention",
        attrs: {
          id: "projectAddress",
          label: "Project Address",
        },
      },
      {
        type: "text",
        text: " ",
      },
    ],
  },
  {
    type: "spacerBlock",
    attrs: {
      height: 10,
    },
  },
  {
    type: "paragraph",
    attrs: {
      textAlign: "left",
    },
    content: [
      {
        type: "text",
        marks: [
          {
            type: "bold",
          },
        ],
        text: "PREPARED FOR / ON BEHALF OF:",
      },
    ],
  },
  {
    type: "paragraph",
    attrs: {
      textAlign: "left",
    },
    content: [
      {
        type: "mention",
        attrs: {
          id: "client",
          label: "Project Client",
        },
      },
      {
        type: "text",
        text: " ",
      },
    ],
  },
  {
    type: "spacerBlock",
    attrs: {
      height: 10,
    },
  },
  {
    type: "paragraph",
    attrs: {
      textAlign: "left",
    },
    content: [
      {
        type: "text",
        marks: [
          {
            type: "bold",
          },
        ],
        text: "PREPARED BY:",
      },
    ],
  },
  {
    type: "paragraph",
    attrs: {
      textAlign: "left",
    },
    content: [
      {
        type: "mention",
        attrs: {
          id: "userName",
          label: "Created By User",
        },
      },
      {
        type: "text",
        text: " ",
      },
    ],
  },
  {
    type: "paragraph",
    attrs: {
      textAlign: "left",
    },
    content: [
      {
        type: "mention",
        attrs: {
          id: "userPosition",
          label: "Created By User Position",
        },
      },
      {
        type: "text",
        text: " ",
      },
    ],
  },
  {
    type: "spacerBlock",
    attrs: {
      height: 10,
    },
  },
  {
    type: "paragraph",
    attrs: {
      textAlign: "center",
    },
    content: [
      {
        type: "text",
        marks: [
          {
            type: "bold",
          },
        ],
        text: "DATE: ",
      },
      {
        type: "mention",
        attrs: {
          id: "createDate",
          label: "Create Date",
        },
      },
      {
        type: "text",
        text: " ",
      },
    ],
  },
];
